import { useState } from "react";
import { OrgPicker } from "@/components/OrgPicker";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
	Search,
	CheckCircle2,
	XCircle,
	AlertTriangle,
	User,
	Shield,
	Package,
	CreditCard,
	ChevronRight,
	Lightbulb,
} from "lucide-react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import * as api from "@/services/api";

export function AccessDebugger() {
	const [selectedOrgId, setSelectedOrgId] = useState<string>("");
	const [selectedUserId, setSelectedUserId] = useState<string>("");
	const [feature, setFeature] = useState<string>("");
	const [action, setAction] = useState<string>("");
	const [userSearch, setUserSearch] = useState("");

	const { data: orgs } = useQuery({
		queryKey: ["organizations"],
		queryFn: api.getOrganizations,
	});

	const { data: users } = useQuery({
		queryKey: ["users", selectedOrgId],
		queryFn: () => api.getUsers(selectedOrgId),
		enabled: !!selectedOrgId,
	});

	const { data: features } = useQuery({
		queryKey: ["features"],
		queryFn: api.getFeatures,
	});

	const debugMutation = useMutation({
		mutationFn: api.debugPermission,
	});

	const selectedUser = users?.find((u) => u._id === selectedUserId);
	const selectedFeature = features?.find((f) => f.slug === feature);

	const filteredUsers = users?.filter(
		(u) =>
			!userSearch ||
			u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
			u.name?.toLowerCase().includes(userSearch.toLowerCase()),
	);

	const handleDebug = () => {
		if (!selectedUserId || !selectedOrgId || !feature || !action) return;
		debugMutation.mutate({
			userId: selectedUserId,
			organizationId: selectedOrgId,
			feature,
			action,
		});
	};

	const result = debugMutation.data;

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold text-gray-900">403 Access Debugger</h1>
				<p className="text-gray-600 mt-2">
					Find out exactly WHY a user can or cannot access a feature
				</p>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Input Form */}
				<Card>
					<CardHeader>
						<CardTitle>Check Access</CardTitle>
						<CardDescription>
							Select a user, feature, and action to debug
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{/* Organization */}
						<div>
							<label className="text-sm font-medium mb-2 block">
								Organization
							</label>
							<OrgPicker
								orgs={orgs}
								value={selectedOrgId}
								onChange={setSelectedOrgId}
							/>
						</div>

						{/* User */}
						<div>
							<label className="text-sm font-medium mb-2 block">User</label>
							{selectedOrgId ? (
								<>
									<div className="relative mb-2">
										<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
										<Input
											placeholder="Search users..."
											value={userSearch}
											onChange={(e) => setUserSearch(e.target.value)}
											className="pl-10"
										/>
									</div>
									<Select
										value={selectedUserId}
										onValueChange={setSelectedUserId}
									>
										<SelectTrigger>
											<SelectValue placeholder="Select user..." />
										</SelectTrigger>
										<SelectContent>
											{filteredUsers?.slice(0, 20).map((user) => (
												<SelectItem key={user._id} value={user._id}>
													<div className="flex flex-col">
														<span>{user.name || user.email}</span>
														<span className="text-xs text-muted-foreground">
															{user.email} - {user.role}
														</span>
													</div>
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									{selectedUser && (
										<div className="mt-2 p-3 bg-blue-50 rounded-lg">
											<div className="flex items-center gap-2">
												<User className="w-4 h-4 text-blue-600" />
												<span className="font-medium">
													{selectedUser.name || selectedUser.email}
												</span>
												<Badge variant="secondary">{selectedUser.role}</Badge>
											</div>
										</div>
									)}
								</>
							) : (
								<p className="text-sm text-muted-foreground">
									Select an organization first
								</p>
							)}
						</div>

						{/* Feature */}
						<div>
							<label className="text-sm font-medium mb-2 block">Feature</label>
							<Select value={feature} onValueChange={setFeature}>
								<SelectTrigger>
									<SelectValue placeholder="Select feature..." />
								</SelectTrigger>
								<SelectContent>
									{features?.map((f) => (
										<SelectItem key={f.slug} value={f.slug}>
											{f.ui?.label || f.slug}
											<span className="text-xs text-muted-foreground ml-2">
												({f.module})
											</span>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Action */}
						<div>
							<label className="text-sm font-medium mb-2 block">Action</label>
							<Select
								value={action}
								onValueChange={setAction}
								disabled={!feature}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select action..." />
								</SelectTrigger>
								<SelectContent>
									{selectedFeature?.actions.map((a) => (
										<SelectItem key={a} value={a}>
											{a}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<Button
							onClick={handleDebug}
							disabled={
								!selectedUserId ||
								!selectedOrgId ||
								!feature ||
								!action ||
								debugMutation.isPending
							}
							className="w-full"
						>
							{debugMutation.isPending ? "Checking..." : "Debug Access"}
						</Button>
					</CardContent>
				</Card>

				{/* Results */}
				<div className="space-y-4">
					{/* Result Card */}
					{result && (
						<Card
							className={
								result.result.allowed
									? "border-green-200 bg-green-50"
									: "border-red-200 bg-red-50"
							}
						>
							<CardContent className="pt-6">
								<div className="flex items-center gap-4">
									{result.result.allowed ? (
										<div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
											<CheckCircle2 className="w-8 h-8 text-green-600" />
										</div>
									) : (
										<div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
											<XCircle className="w-8 h-8 text-red-600" />
										</div>
									)}
									<div>
										<h3
											className={`text-xl font-bold ${result.result.allowed ? "text-green-800" : "text-red-800"}`}
										>
											{result.result.allowed ? "Access Granted" : "Access Denied"}
										</h3>
										<p
											className={`text-sm ${result.result.allowed ? "text-green-600" : "text-red-600"}`}
										>
											{feature}:{action}
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
					)}

					{/* Debug Chain */}
					{result && (
						<Card>
							<CardHeader>
								<CardTitle className="text-lg">Permission Chain</CardTitle>
								<CardDescription>
									Step-by-step permission resolution
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* Step 1: Role Check */}
								<div className="flex items-start gap-3">
									<div
										className={`w-8 h-8 rounded-full flex items-center justify-center ${
											result.roleChain.some((r) => r.hasFeature && r.hasAction)
												? "bg-green-100"
												: "bg-red-100"
										}`}
									>
										<Shield
											className={`w-4 h-4 ${
												result.roleChain.some(
													(r) => r.hasFeature && r.hasAction,
												)
													? "text-green-600"
													: "text-red-600"
											}`}
										/>
									</div>
									<div className="flex-1">
										<div className="flex items-center gap-2">
											<span className="font-medium">Role Permission</span>
											{result.roleChain.some(
												(r) => r.hasFeature && r.hasAction,
											) ? (
												<CheckCircle2 className="w-4 h-4 text-green-600" />
											) : (
												<XCircle className="w-4 h-4 text-red-600" />
											)}
										</div>
										<div className="mt-2 space-y-1">
											{result.roleChain.map((role) => (
												<div
													key={role.roleId}
													className="flex items-center gap-2 text-sm"
												>
													<ChevronRight className="w-3 h-3 text-gray-400" />
													<span>{role.roleName}</span>
													<Badge
														variant={
															role.hasFeature && role.hasAction
																? "success"
																: "secondary"
														}
													>
														{role.hasFeature && role.hasAction
															? "Has permission"
															: role.hasFeature
																? `Missing ${action}`
																: "Missing feature"}
													</Badge>
												</div>
											))}
										</div>
									</div>
								</div>

								{/* Step 2: Plan Check */}
								<div className="flex items-start gap-3">
									<div
										className={`w-8 h-8 rounded-full flex items-center justify-center ${
											result.planCheck.hasFeature
												? "bg-green-100"
												: "bg-red-100"
										}`}
									>
										<Package
											className={`w-4 h-4 ${
												result.planCheck.hasFeature
													? "text-green-600"
													: "text-red-600"
											}`}
										/>
									</div>
									<div className="flex-1">
										<div className="flex items-center gap-2">
											<span className="font-medium">Plan Feature</span>
											{result.planCheck.hasFeature ? (
												<CheckCircle2 className="w-4 h-4 text-green-600" />
											) : (
												<XCircle className="w-4 h-4 text-red-600" />
											)}
										</div>
										<div className="mt-1 space-y-1">
											<p className="text-sm text-muted-foreground">
												Base plan "{result.planCheck.plan}"{" "}
												{result.planCheck.inBasePlan ? "includes" : "does not include"}{" "}
												{feature}
											</p>
											{result.planCheck.customPackage && (
												<div className="text-sm">
													{result.planCheck.customPackage.isCustomAdded && (
														<p className="text-blue-600">
															✚ Added via custom package
														</p>
													)}
													{result.planCheck.customPackage.isBlocked && (
														<p className="text-red-600">
															⛔ Blocked by custom package
														</p>
													)}
												</div>
											)}
										</div>
									</div>
								</div>

								{/* Step 3: Subscription Check */}
								<div className="flex items-start gap-3">
									<div
										className={`w-8 h-8 rounded-full flex items-center justify-center ${
											result.subscriptionCheck.isActive
												? "bg-green-100"
												: "bg-red-100"
										}`}
									>
										<CreditCard
											className={`w-4 h-4 ${
												result.subscriptionCheck.isActive
													? "text-green-600"
													: "text-red-600"
											}`}
										/>
									</div>
									<div className="flex-1">
										<div className="flex items-center gap-2">
											<span className="font-medium">Subscription Status</span>
											{result.subscriptionCheck.isActive ? (
												<CheckCircle2 className="w-4 h-4 text-green-600" />
											) : (
												<XCircle className="w-4 h-4 text-red-600" />
											)}
										</div>
										<p className="text-sm text-muted-foreground">
											Status: {result.subscriptionCheck.status}
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
					)}

					{/* Suggestions */}
					{result && result.suggestions.length > 0 && (
						<Card className="border-yellow-200 bg-yellow-50">
							<CardHeader>
								<CardTitle className="text-lg flex items-center gap-2">
									<Lightbulb className="w-5 h-5 text-yellow-600" />
									Suggestions
								</CardTitle>
							</CardHeader>
							<CardContent>
								<ul className="space-y-2">
									{result.suggestions.map((suggestion, i) => (
										<li
											key={i}
											className="flex items-start gap-2 text-sm text-yellow-800"
										>
											<AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
											{suggestion}
										</li>
									))}
								</ul>
							</CardContent>
						</Card>
					)}

					{/* Empty State */}
					{!result && !debugMutation.isPending && (
						<Card className="border-dashed">
							<CardContent className="py-12 text-center">
								<Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
								<h3 className="font-medium text-gray-600">
									Select a user, feature, and action
								</h3>
								<p className="text-sm text-muted-foreground mt-1">
									Then click "Debug Access" to see the permission chain
								</p>
							</CardContent>
						</Card>
					)}
				</div>
			</div>
		</div>
	);
}
